/**
 * Toolkit Detail (P27 + P30 strip).
 *
 * Looks up a server by slug via the catalog list, then fetches its tools.
 * Wireframe Tabs (Sessions/Auth/Policy/Triggers) and the static-block
 * cards (SchemaPreview/AuthSummary/PolicySteps) were removed because none
 * of them have backing APIs in V1. What's left are the two slices that DO
 * have data: server metadata + the actual list of registered tools.
 */
import { useEffect, useMemo, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, Box } from 'lucide-react';
import type { McpTool } from '@sumcp/shared-types';

import { useServerBySlug, useToolsForServer } from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { useBreadcrumb } from '@/components/console/BreadcrumbContext.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { StatusPill, statusToneFrom } from '@/components/console/StatusPill.js';
import { AsyncShell } from '@/components/console/StateBlocks.js';
import { ToolDetail } from '@/components/console/drilldowns/ToolDetail.js';
import { useUrlParam } from '@/hooks/useUrlState.js';

export default function ToolkitDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const lookup = useServerBySlug(slug);
  const tools = useToolsForServer(lookup.data?.id);
  // Tool drawer in URL → back closes the drawer; deep-linking to a tool works.
  const [toolParam, setToolParam] = useUrlParam('tool', '');
  const selectedToolName = toolParam || null;
  const setSelectedToolName = (name: string | null): void => setToolParam(name ?? '');
  const { setDetailLabel } = useBreadcrumb();
  useEffect(() => {
    setDetailLabel(lookup.data?.name ?? slug ?? null);
    return () => setDetailLabel(null);
  }, [lookup.data?.name, slug, setDetailLabel]);
  const selectedTool = useMemo(
    () => tools.data?.items.find((t) => t.name === selectedToolName) ?? null,
    [tools.data?.items, selectedToolName],
  );

  return (
    <>
      <PageHeader
        eyebrow="Toolkit detail"
        title={lookup.data?.name ?? slug ?? 'Toolkit'}
        description={
          lookup.data
            ? `${lookup.data.owningWgId} · ${lookup.data.backendType} · risk: ${lookup.data.riskTier}`
            : 'Loading…'
        }
      >
        {lookup.data && (
          <Link to={`/build/servers/${lookup.data.id}`}>
            <ActionButton icon={ArrowRight} primary>
              Open publisher view
            </ActionButton>
          </Link>
        )}
      </PageHeader>

      <AsyncShell
        isLoading={lookup.isLoading}
        isError={lookup.isError}
        isEmpty={!lookup.data}
        loadingTitle="Loading toolkit"
        emptyTitle="Toolkit not found"
        emptyHint={
          <>
            No active server with slug <code>{slug}</code> in the catalog.
          </>
        }
      >
        {lookup.data && (
          <>
            <SectionCard
              icon={Box}
              title="Server metadata"
              text="Live data from the gateway registry."
            >
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  rowGap: 8,
                  columnGap: 16,
                  margin: 0,
                  fontSize: 15,
                }}
              >
                <dt className="subtle">Slug</dt>
                <dd>
                  <code>{lookup.data.slug}</code>
                </dd>
                <dt className="subtle">Owning WG</dt>
                <dd>{lookup.data.owningWgId}</dd>
                <dt className="subtle">Backend</dt>
                <dd>
                  <code>{lookup.data.backendUrl}</code>
                </dd>
                <dt className="subtle">Backend type</dt>
                <dd>{lookup.data.backendType}</dd>
                <dt className="subtle">Environment</dt>
                <dd>{lookup.data.environment}</dd>
                <dt className="subtle">Risk tier</dt>
                <dd>{lookup.data.riskTier}</dd>
                <dt className="subtle">Status</dt>
                <dd>
                  <StatusPill
                    label={lookup.data.status}
                    tone={statusToneFrom(lookup.data.status)}
                  />
                </dd>
                <dt className="subtle">Created</dt>
                <dd>{new Date(lookup.data.createdAt).toLocaleString()}</dd>
              </dl>
            </SectionCard>

            <SectionCard
              icon={Box}
              title={`Registered tools (${tools.data?.items.length ?? 0})`}
              text="Each tool inherits the server's risk tier and audit policy."
            >
              <AsyncShell
                isLoading={tools.isLoading}
                isError={tools.isError}
                isEmpty={(tools.data?.items.length ?? 0) === 0}
                error={tools.error}
                loadingTitle="Loading tools"
                emptyTitle="No tools registered yet"
                emptyHint={<>No tools registered yet for this server.</>}
              >
                <DataTable
                  columns={['name', 'display', 'data', 'side_effect', 'status', 'version']}
                  rows={(tools.data?.items ?? []).map(toolToRow) as readonly DataRow[]}
                  onRow={(r) => setSelectedToolName(String(r.name))}
                />
              </AsyncShell>
            </SectionCard>
          </>
        )}
      </AsyncShell>

      <ToolDetail
        open={selectedTool != null}
        onClose={() => setSelectedToolName(null)}
        tool={selectedTool}
      />
    </>
  );
}

function toolToRow(t: McpTool): DataRow {
  return {
    name: t.name ?? '—',
    display: t.display_name ?? t.name ?? '—',
    data: t.data_classification ?? '—',
    side_effect: t.side_effect_level ?? '—',
    status: t.status ?? '—',
    version: (t.version_hash ?? '').slice(0, 12) || '—',
  };
}
